<div class="bg-gray-50 min-h-screen p-2 ">
  <!-- Header Section -->
  <div class="bg-white rounded-lg shadow-sm p-4 mb-6">
    <div class="flex flex-col md:flex-row md:items-center md:justify-between">
      <div class="mb-4 md:mb-0">
        <h1 class="text-2xl font-bold text-gray-900">Product Management</h1>
        <p class="text-gray-600 mt-1">Manage products across the system.</p>
      </div>
      <div>
        <button 
          *hasPermission="'product.create'"
          pButton 
          type="button" 
          label="Add New Product" 
          icon="pi pi-plus" 
          class="p-button-primary"
          (click)="addNewProduct()"
          aria-label="Add new product to the system" i18n-aria-label
        ></button>
      </div>
    </div>
  </div>

  <!-- Filters Section -->
  <div class="bg-white rounded-lg shadow-sm p-4 mb-6">
    <div class="flex flex-col flex-wrap lg:flex-row lg:items-center lg:justify-between gap-4">
      <div class="flex-1">
        <div class="flex flex-wrap gap-2">
          <label for="product-search" class="sr-only">Search products</label>
          <p-iconfield iconPosition="left" class="flex-1">
            <p-inputicon>
              <i class="pi pi-search" aria-hidden="true"></i>
            </p-inputicon>
            <input 
              pInputText 
              type="text" 
              id="product-search"
              placeholder="Search product by name..." i18n-placeholder
              [(ngModel)]="searchText" 
              (keyup.enter)="onSearch()" 
              class="min-w-[200px]" 
              fluid
              aria-label="Search products by name or other details" i18n-aria-label>
          </p-iconfield>
          
          <button 
            pButton 
            type="button" 
            icon="pi pi-search" 
            class="p-button-primary p-button-sm"
            (click)="onSearch()"
            [disabled]="loading"
            [attr.aria-busy]="loading"
            label="Search"
            aria-label="Search products" i18n-aria-label
            pTooltip="Search products" i18n-pTooltip
          ></button>
          
          <button 
            pButton 
            type="button" 
            icon="pi pi-refresh" 
            label="Reset"
            class="p-button-secondary p-button-sm"
            (click)="resetFilters()"
            [disabled]="loading"
            aria-label="Reset all filters to default values" i18n-aria-label
            pTooltip="Reset all filters" i18n-pTooltip
          ></button>
        </div>
      </div>
      
      <div class="flex flex-col sm:flex-row gap-4">
        <div class="flex flex-col">
          <!-- <label class="text-sm font-medium text-gray-700 mb-2">Status:</label> -->
          <p-selectButton 
            [options]="statusOptions" 
            [(ngModel)]="selectedStatus"
            (onChange)="onFilterChange()"
            optionLabel="label"
            optionValue="value"
            styleClass="p-button-sm"
            [attr.aria-label]="'Filter products by status'"
          ></p-selectButton>
        </div>
        
        <div class="flex flex-col">
          <!-- <label class="text-sm font-medium text-gray-700 mb-2">Archived:</label> -->
          <p-selectButton 
            [options]="getArchivedOptions()" 
            [(ngModel)]="selectedArchived"
            (onChange)="onArchivedChange($event)"
            optionLabel="label"
            optionValue="value"
            styleClass="p-button-sm"
            [attr.aria-label]="'Filter products by archived status'"
          ></p-selectButton>
        </div>

        
      </div>
    </div>
  </div>

  <!-- Products Table -->
  <div class="bg-white rounded-lg shadow-sm p-4">
    <p-table 
      [value]="products" 
      [loading]="loading"
      [paginator]="false"
      [rows]="pageSize"
      [showCurrentPageReport]="true"
      [tableStyle]="{'min-width': '75rem'}"
      [rowHover]="false"
    >
      <!-- Product Name Column -->
      <ng-template pTemplate="header">
        <tr>
          <th class="w-1/5">Product Name</th>
          <th class="w-1/6">PRICE</th>
          <th class="w-1/6">RESERVATIONS</th>
          <th class="w-1/6">AVAILABLE TERRITORIES</th>
          <th class="w-1/6">STATUS</th>
          <th class="w-1/6 cursor-pointer hover:bg-gray-50 transition-colors duration-200" (click)="toggleSort()">
            <div class="flex items-center justify-between">
              <span>CREATED</span>
              <i class="pi" [ngClass]="sortOrder === -1 ? 'pi-sort-down' : 'pi-sort-up'"></i>
            </div>
          </th>
          <th class="w-20">ACTIONS</th>
        </tr>
      </ng-template>

      <!-- Product Rows -->
      <ng-template pTemplate="body" let-product>
        <tr [pSelectableRow]="product" class="hover:bg-gray-50">
          <td class="py-4">
            <div>
              <div class="font-semibold text-gray-900">{{ product.name }}</div>
              <div class="text-sm text-gray-500">{{ product.refNo || product.code }}</div>
            </div>
          </td>
          <td class="py-4">
            <div class="font-medium text-gray-900">{{ getPriceDisplay(product) }}</div>
          </td>
          <td class="py-4">
            <div class="text-gray-900">{{ getReservationsDisplay(product) }}</div>
          </td>
          <td class="py-4">
            <div class="text-gray-900">{{ getAvailableTerritoriesCount(product) }}</div>
          </td>
          <td class="py-4">
            <p-tag 
              [value]="getStatusLabel(product)" 
              [severity]="getStatusSeverity(product)"
              [rounded]="true"
            ></p-tag>
          </td>
          <td class="py-4">
            <div class="text-gray-900">{{ formatDate(product.createdAt) }}</div>
          </td>
          <td class="py-4">
            <div class="text-center">
              <button 
                pButton 
                type="button" 
                icon="pi pi-ellipsis-v" 
                class="p-button-text p-button-rounded p-button-sm"
                (click)="onActionClick($event, product)"
                [attr.aria-label]="'More actions for product ' + product.name"
                [attr.aria-haspopup]="true"
                [attr.aria-expanded]="false"
                pTooltip="Product actions"
              ></button>
            </div>
          </td>
        </tr>
      </ng-template>

      <!-- Empty State -->
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="7" class="text-center p-8">
            <div class="text-gray-500" role="status">
              <i class="pi pi-box text-4xl mb-4 block" aria-hidden="true"></i>
              <h3 class="text-lg font-medium mb-2">No Products Found</h3>
              <p class="mb-4">No products match your current filters.</p>
              
            </div>
          </td>
        </tr>
      </ng-template>
    </p-table>

    <!-- Pagination -->
    <nav class="border-t border-gray-200 p-4" *ngIf="totalProducts > 0" aria-label="Product list pagination">
      <p-paginator 
        [rows]="pageSize" 
        [totalRecords]="totalProducts"
        [showCurrentPageReport]="true"
        [rowsPerPageOptions]="pageSizes"
        [showPageLinks]="true"
        [showFirstLastIcon]="true"
        (onPageChange)="onPageChange($event)"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} products"
        rowsPerPageReportTemplate="{first} to {last} of {totalRecords}"
        [attr.aria-label]="'Navigate through product pages'"
      ></p-paginator>
    </nav>
  </div>

  <!-- Action Menu -->
  <p-menu 
    #menu 
    [popup]="true" 
    [model]="actionMenuItems"
    role="menu"
    [attr.aria-label]="'Product actions menu'">
  </p-menu>

  <!-- Product Action Loading Overlay -->
  <div *ngIf="performingProductAction" class="fixed inset-0 bg-white bg-opacity-90 flex items-center justify-center z-50">
    <app-loading-spinner size="large" type="full-screen" message="Processing product action..."></app-loading-spinner>
  </div>

  <!-- Confirmation Dialog -->
  <p-confirmDialog 
    [attr.aria-label]="'Confirmation dialog'"
    role="alertdialog">
  </p-confirmDialog>

  <!-- Toast Messages -->
  <p-toast></p-toast>

  <!-- Product Details Drawer -->
  <p-drawer 
    [(visible)]="isDrawerVisible" 
    position="right" 
    [modal]="true" 
    styleClass="!w-full md:!w-[30rem] lg:!w-[60rem]"
    [closable]="true"
    role="dialog"
    [attr.aria-label]="'Product details panel'"
    [attr.aria-modal]="true"
  >
    <ng-template pTemplate="header">
      <h4 class="text-lg font-semibold" id="product-details-heading">Product Information</h4>
    </ng-template>
    
    <ng-template pTemplate="content">
      <main aria-labelledby="product-details-heading">
      <!-- Product content when available -->
      <div *ngIf="selectedProductForReview" class="space-y-6">
        <div class="flex flex-row justify-between">
          <div>
            <h3 class="text-xl font-bold text-gray-900 mb-2">{{ selectedProductForReview.name }}</h3>
            <p class="text-sm text-gray-500">{{ selectedProductForReview.refNo || selectedProductForReview.code }}</p>
          </div>
          
          <div>
            <p-tag 
              [value]="getStatusLabel(selectedProductForReview)" 
              [severity]="getStatusSeverity(selectedProductForReview)"
              [rounded]="true"
            ></p-tag>
          </div>
        </div>

        
        <div class="mb-4 flex flex-row justify-between w-full bg-blue-100 p-4 rounded-lg items-center">
          <label class="block text-sm font-medium text-gray-700">Price:</label>
          <p class="text-gray-900">{{ getPriceDisplay(selectedProductForReview) }}</p>
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Reservations:</label>
          <p class="text-gray-900">{{ getReservationsDisplay(selectedProductForReview) }}</p>
        </div>
        
        <div class="p-4 border border-gray-200 rounded-lg">
          <label class="block text-sm font-medium text-gray-700 mb-1">Description:</label>
          <div class="text-gray-900 break-words overflow-wrap-anywhere max-w-full" [innerHTML]="sanitizeHtml(selectedProductForReview.description || '')"></div>
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Available Territories:</label>
          <div class="mt-2">
            <p-accordion [value]="0">
              <p-accordion-panel 
                *ngFor="let region of getTerritoriesByRegion(selectedProductForReview); let i = index" 
                [value]="i"
              >
                <p-accordion-header>{{ region }}</p-accordion-header>
                <p-accordion-content>
                  <div class="space-y-2">
                    <div 
                      *ngFor="let territory of getTerritoriesByRegionAndName(selectedProductForReview, region)" 
                      class="flex items-center"
                    >
                      <i class="pi pi-check-circle text-green-500 mr-2"></i>
                      <span class="text-gray-900">{{ territory.name }}</span>
                    </div>
                  </div>
                </p-accordion-content>
              </p-accordion-panel>
            </p-accordion>
          </div>
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Company:</label>
          <p class="text-gray-900">{{ selectedProductForReview.subscriberId.companyName || 'N/A' }}</p>
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Created:</label>
          <p class="text-gray-900">{{ formatDate(selectedProductForReview.createdAt) }}</p>
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Last Updated:</label>
          <p class="text-gray-900">{{ formatDate(selectedProductForReview.updatedAt) }}</p>
        </div>
      </div>
      
      <!-- Fallback when no product selected -->
      @if (!selectedProductForReview) {
        <div class="text-center py-8">
          <i class="pi pi-exclamation-triangle text-4xl text-gray-400 mb-4 block" aria-hidden="true"></i>
          <h3 class="text-lg font-medium text-gray-600 mb-2">No Product Selected</h3>
          <p class="text-gray-500">Please select a product to view its details.</p>
          </div>
      }
      </main>
    </ng-template>
  </p-drawer>
</div>
